export type OnboardingStatus = {
    hasUserProfile: boolean;
    hasHostProfile: boolean;
    hostType: "INDIVIDUAL" | "COMPANY" | null;
};

export type City = {
    id: string;
    name: string;
};

export type Category = {
    id: string;
    name: string;
    color?: string | null;
};

export type SearchEvent = {
    id: string;
    shortId?: string | null;
    title: string;
    date?: string | null;
    hostName?: string | null;
    locationName?: string | null;
    city?: string | null;
};

export type NavItem = {
    id: string;
    label: string;
    href: string;
    hasCaret?: boolean;
};

export const defaultCities: City[] = [];
export const CITY_STORAGE_KEY = "hopp:landing-city";

export const fallbackNavItems: NavItem[] = [
    { id: "events", label: "Events", href: "/events", hasCaret: true },
    { id: "ladies-night", label: "Ladies Night", href: "#" },
    { id: "house-parties", label: "House Parties", href: "#" },
    { id: "live-music", label: "Live Music", href: "#" },
    { id: "popular-shows", label: "Popular Shows", href: "#" },
    { id: "nightlife", label: "Nightlife", href: "#" },
    { id: "desi", label: "Desi", href: "#" },
    { id: "business-events", label: "Business Events", href: "#" },
    { id: "comedy", label: "Comedy", href: "#", hasCaret: true },
];

export const formatSuggestionDate = (value?: string | null) => {
    if (!value) return "Date TBA";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "Date TBA";
    return date.toLocaleDateString("en-GB", {
        month: "short",
        day: "numeric",
    });
};

export const formatNotificationTime = (value: Date | string) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
    return date.toLocaleDateString("en-GB", { month: "short", day: "numeric" });
};

export const formatMonthLabel = (year: number, month: number) =>
    new Date(Date.UTC(year, month, 1)).toLocaleDateString("en-GB", {
        month: "long",
        year: "numeric",
    });

export const toDateKey = (value: Date) => value.toISOString().slice(0, 10);

export const parseDateKey = (value: string) => {
    if (!value) return null;
    const parsed = new Date(`${value}T00:00:00.000Z`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const buildCalendarCells = (year: number, month: number) => {
    const firstOfMonth = new Date(Date.UTC(year, month, 1));
    const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    const startDay = (firstOfMonth.getUTCDay() + 6) % 7;
    const cells: { date: Date; inMonth: boolean }[] = [];
    const prevMonthDays = startDay;
    for (let i = prevMonthDays; i > 0; i -= 1) {
        const date = new Date(Date.UTC(year, month, 1 - i));
        cells.push({ date, inMonth: false });
    }
    for (let day = 1; day <= daysInMonth; day += 1) {
        const date = new Date(Date.UTC(year, month, day));
        cells.push({ date, inMonth: true });
    }
    const remainder = cells.length % 7;
    const trailing = remainder === 0 ? 0 : 7 - remainder;
    for (let i = 1; i <= trailing; i += 1) {
        const date = new Date(Date.UTC(year, month, daysInMonth + i));
        cells.push({ date, inMonth: false });
    }
    return cells;
};
